/* global google, fetch */
import { BikehubMapping } from '../utils/mapping.js'

// Washington DC metro area
const DC_CENTER = { lat: 38.9072, lng: -77.0369, zoom: 11 }

const REFRESH_MS = 30 * 60 * 1000
const stationsUrl = () => window.wmataStationsUrl || '/dashboard/stations'

const STATUS_ICONS = {
  green: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  amber: 'http://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
  red: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png'
}

// WMATA line colors, with text color chosen for contrast on each background
const LINE_STYLES = {
  Red: { bg: '#BA0C2F', fg: '#fff' },
  Blue: { bg: '#009CDE', fg: '#fff' },
  Orange: { bg: '#ED8B00', fg: '#fff' },
  Silver: { bg: '#919D9D', fg: '#000' },
  Yellow: { bg: '#FFD100', fg: '#000' },
  Green: { bg: '#00B140', fg: '#fff' }
}

function escapeHtml (value) {
  return String(value == null ? '' : value).replace(/[&<>"']/g, ch => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[ch]))
}

export class WmataDashboard {
  constructor () {
    const data = window.wmataDashboardData || {}
    this.stations = data.stations || []
    this.sla = data.sla || {}
    this.staticAssets = data.static_assets || {}
    this.maintenance = data.maintenance || {}
    this.uptimeThreshold = data.uptime_threshold
    this.mapping = new BikehubMapping('wmata_dashboard')
    this.mapping.mapElementID = 'wmataDashboardMap'
    this.markers = []
    this.activeStationId = null
  }

  init () {
    this.renderSummary()
    if (!this.mapping.googleMapsLoaded()) {
      return this.mapping.loadMap(() => this.renderAll())
    }
    this.renderAll()
  }

  renderAll () {
    this.mapping.renderMap({
      lat: DC_CENTER.lat,
      lng: DC_CENTER.lng,
      zoom: DC_CENTER.zoom,
      storeLocation: false,
      renderAddressSearch: false
    })
    this.renderMarkers(true)
    this.startAutoRefresh()
  }

  startAutoRefresh () {
    if (this.refreshTimer) { return }
    this.refreshTimer = setInterval(() => this.refresh(), REFRESH_MS)
  }

  // Poll for fresh data and update the map and open drawer in place.
  refresh () {
    fetch(stationsUrl(), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then(data => {
        this.stations = (data && data.stations) || []
        this.sla = (data && data.sla) || {}
        this.staticAssets = (data && data.static_assets) || {}
        this.maintenance = (data && data.maintenance) || {}
        this.uptimeThreshold = data && data.uptime_threshold
        this.renderMarkers(false)
        this.renderSummary()
        if (this.activeStationId) {
          const active = this.stations.find(s => s.id === this.activeStationId)
          if (active) { this.openStation(active) }
        }
      })
      .catch(() => { /* keep last known data on failure */ })
  }

  renderMarkers (fitMap) {
    // Clear existing markers before re-rendering
    this.markers.forEach(m => m.setMap(null))
    this.markers = this.stations
      .filter(station => station.lat != null && station.lng != null)
      .map(station => {
        const marker = new google.maps.Marker({
          position: new google.maps.LatLng(station.lat, station.lng),
          map: window.bikehubMap,
          title: station.name,
          icon: {
            url: STATUS_ICONS[station.status] || STATUS_ICONS.amber,
            scaledSize: new google.maps.Size(40, 40)
          },
          ourId: station.id
        })
        marker.addListener('click', () => this.openStation(station))
        return marker
      })
    if (fitMap && this.markers.length) {
      const bounds = new google.maps.LatLngBounds()
      this.markers.forEach(m => bounds.extend(m.getPosition()))
      window.bikehubMap.fitBounds(bounds)
    }
  }

  openStation (station) {
    const drawer = document.getElementById('wmataStationDrawer')
    if (!drawer) { return }
    this.activeStationId = station.id
    drawer.innerHTML = this.drawerHtml(station)
  }

  // Derived from the station data so it updates together with the refresh.
  renderSummary () {
    const el = document.getElementById('wmataSummaryBar')
    if (!el) { return }
    const stations = this.stations || []
    const total = stations.length
    const withIssues = stations.filter(s => s.status !== 'green' || s.high_unknown).length
    const active = total - withIssues
    const totalSpaces = stations.reduce((n, s) => n + (s.total_spaces || 0), 0)
    const availSum = stations.reduce((n, s) => n + ((s.availability && s.availability.available) || 0), 0)
    const totalSum = stations.reduce((n, s) => n + ((s.availability && s.availability.total) || 0), 0)
    const pct = totalSum > 0 ? Math.round((availSum / totalSum) * 100) : 0
    el.innerHTML = `
      ${this.slaBanner()}
      <div class="row">
        ${this.metricCard('Total stations', total, '#e7f1ff')}
        ${this.metricCard('Active', active, '#e6f4ea')}
        ${this.metricCard('Offline / issues', withIssues, '#fdecea')}
        ${this.metricCard('Total spaces', totalSpaces, '#eef2ff')}
        ${this.metricCard('Availability', pct + '%', '#fff7e6')}
        ${this.staticAssetsCard()}
      </div>
      <div class="small text-muted mt-1">BikeLink assets &middot; As of ${this.formatTime(this.latestTimestamp(stations))}</div>
      ${this.slaSummaryRow()}`
  }

  // Stations whose last completed maintenance is older than the SLA interval.
  maintenanceCard () {
    const m = this.maintenance || {}
    if (!m.threshold_days) { return '' }
    const overdue = (m.stations_overdue || 0) + (m.stations_never_serviced || 0)
    const bg = overdue > 0 ? '#fdecea' : ((m.stations_at_risk || 0) > 0 ? '#fff7e6' : '#e6f4ea')
    return `<div class="col mb-2">
      <div class="border rounded text-center py-2 h-100" style="background-color: ${bg};">
        <div class="h4 mb-0">${overdue}</div>
        <div class="small text-muted">Maintenance overdue</div>
        <div class="small text-muted">every ${m.threshold_days} days</div>
      </div>
    </div>`
  }

  // Top-of-dashboard alert banner when work orders are at risk of or past SLA.
  slaBanner () {
    const s = this.sla || {}
    if (s.status === 'red' && (s.open_breaching || 0) > 0) {
      const n = s.open_breaching
      return `<div class="alert alert-danger py-2 mb-2" role="alert"><strong>SLA breach:</strong> ${n} open work order${n === 1 ? '' : 's'} past the response-time SLA.</div>`
    }
    if (s.status === 'amber' && (s.open_at_risk || 0) > 0) {
      const n = s.open_at_risk
      return `<div class="alert alert-warning py-2 mb-2" role="alert"><strong>SLA at risk:</strong> ${n} open work order${n === 1 ? '' : 's'} approaching the response-time SLA.</div>`
    }
    return ''
  }

  // SLA compliance cards, colored green/amber/red by the program status.
  slaSummaryRow () {
    const s = this.sla || {}
    if (!Object.keys(s).length) { return '' }
    const statusBg = { green: '#e6f4ea', amber: '#fff7e6', red: '#fdecea' }
    const pct = (s.compliance_percent == null) ? 'n/a' : s.compliance_percent + '%'
    return `
      <div class="row mt-2">
        ${this.metricCard('Within SLA', s.open_within_sla || 0, '#e6f4ea')}
        ${this.metricCard('At risk', s.open_at_risk || 0, '#fff7e6')}
        ${this.metricCard('Breaching SLA', s.open_breaching || 0, '#fdecea')}
        ${this.metricCard('Compliance (MTD)', pct, statusBg[s.status] || '#f8f9fa')}
        ${this.maintenanceCard()}
      </div>
      <div class="small text-muted mt-1">SLA compliance &middot; open work orders vs configured response-time SLA</div>`
  }

  metricCard (label, value, bg) {
    return `<div class="col mb-2">
      <div class="border rounded text-center py-2 h-100" style="background-color: ${bg || '#f8f9fa'};">
        <div class="h4 mb-0">${value}</div>
        <div class="small text-muted">${escapeHtml(label)}</div>
      </div>
    </div>`
  }

  // Inventory mirrored from Sitetracker; condition comes from inspections.
  staticAssetsCard () {
    const s = this.staticAssets || {}
    if (!s.total) {
      return `<div class="col mb-2">
        <div class="border rounded text-center py-2 h-100" style="background-color: #f1f3f5;">
          <div class="h6 mb-0 text-muted">Static assets</div>
          <div class="small text-muted">None synced yet</div>
        </div>
      </div>`
    }
    const flagged = (s.down || 0) + (s.attention || 0)
    const note = flagged > 0 ? `${s.down || 0} out of service, ${s.attention || 0} need attention` : 'all in service'
    return `<div class="col mb-2">
      <div class="border rounded text-center py-2 h-100" style="background-color: ${(s.down || 0) > 0 ? '#fdecea' : '#f1f3f5'};">
        <div class="h4 mb-0">${s.total}</div>
        <div class="small text-muted">Static assets</div>
        <div class="small text-muted">${note}</div>
      </div>
    </div>`
  }

  latestTimestamp (stations) {
    const times = stations
      .map(s => s.availability_updated_at)
      .filter(Boolean)
      .map(t => new Date(t).getTime())
      .filter(t => !isNaN(t))
    return times.length ? new Date(Math.max(...times)).toISOString() : null
  }

  drawerHtml (station) {
    return `
      <h5 class="mb-1">${escapeHtml(station.name)} Station</h5>
      <div class="mb-2">${this.lineBadges(station.metro_lines)}</div>
      ${this.offlineBadge(station)}
      <p class="text-muted small mb-2">${escapeHtml(station.address)}</p>
      <div class="d-flex justify-content-between small mb-3">
        <span>Total spaces: <strong>${station.total_spaces}</strong></span>
        <span>Oversized: <strong>${station.total_oversized_spaces}</strong></span>
      </div>
      ${this.availabilitySummary(station)}
      ${this.uptimeSection(station)}
      ${this.slaSection(station)}
      ${this.maintenanceSection(station)}
      ${this.assetsHtml(station.assets)}
      ${this.staticAssetsSection(station)}
      <p class="text-muted small mt-3 mb-2">Last availability update: ${this.formatTime(station.availability_updated_at)}</p>
      ${this.actionButtons(station)}
      ${this.managementLinks(station.assets)}
    `
  }

  offlineBadge (station) {
    if (!station.offline) { return '' }
    return `<div class="mb-2"><span class="badge badge-danger">Offline since ${this.formatTime(station.availability_updated_at)}</span></div>`
  }

  availabilitySummary (station) {
    const a = station.availability || {}
    const warning = station.high_unknown
      ? '<div class="text-warning small pl-2">⚠ High unknown count (possible connectivity issue)</div>'
      : ''
    return `
      <div class="mb-3">
        <div class="font-weight-bold small mb-1">Live availability <span class="text-muted font-weight-normal">(BikeLink assets only)</span></div>
        <div class="pl-2">${this.countsBadges(a.available || 0, a.unavailable || 0, a.unknown || 0, a.total || 0)}</div>
        ${warning}
      </div>`
  }

  // BikeLink hardware uptime for the station, flagged when below the SLA target.
  uptimeSection (station) {
    if (station.uptime_30d == null && station.uptime_90d == null) { return '' }
    const flagged = this.uptimeThreshold != null && station.uptime_30d != null && station.uptime_30d < this.uptimeThreshold
    const flag = flagged ? ` <span class="badge badge-danger">Below ${this.uptimeThreshold}% target</span>` : ''
    const fmt = v => (v == null ? 'n/a' : v + '%')
    return `
      <div class="mb-3">
        <div class="font-weight-bold small mb-1">Uptime${flag}</div>
        <div class="pl-2 small">
          <span class="mr-3">30 days: <strong>${fmt(station.uptime_30d)}</strong></span>
          <span>90 days: <strong>${fmt(station.uptime_90d)}</strong></span>
        </div>
      </div>`
  }

  // Per-station SLA drilldown: counts plus the at-risk / breaching work orders.
  slaSection (station) {
    const s = station.sla
    if (!s) { return '' }
    const badges = `
      <span class="badge badge-success mr-1 mb-1">${s.within || 0} within</span>
      <span class="badge badge-warning mr-1 mb-1">${s.at_risk || 0} at risk</span>
      <span class="badge badge-danger mb-1">${s.breaching || 0} breaching</span>`
    const issues = (s.issues || []).map(i => this.slaIssueRow(i)).join('')
    return `
      <div class="mb-3">
        <div class="font-weight-bold small mb-1">SLA compliance <span class="text-muted font-weight-normal">(work orders)</span></div>
        <div class="pl-2">${badges}</div>
        ${issues ? `<div class="pl-2 mt-1">${issues}</div>` : ''}
      </div>`
  }

  slaIssueRow (issue) {
    const isBreach = issue.state === 'breaching'
    const cls = isBreach ? 'text-danger' : 'text-warning'
    const label = isBreach ? 'BREACH' : 'At risk'
    const asset = issue.asset_type ? ` &middot; ${escapeHtml(issue.asset_type)}` : ''
    return `
      <div class="small mb-1 ${cls}">
        <strong>${label}</strong> &middot; ${escapeHtml(issue.priority)}${asset} &middot; ${issue.hours_elapsed}h / ${issue.hours_allowed}h
        <div class="text-muted">#${issue.id} ${escapeHtml(issue.description || '')}</div>
      </div>`
  }

  countsBadges (available, unavailable, unknown, total) {
    return `
      <span class="badge badge-success mr-1 mb-1">${available} avail</span>
      <span class="badge badge-secondary mr-1 mb-1">${unavailable} unavail</span>
      <span class="badge badge-warning mr-1 mb-1">${unknown} unknown</span>
      <span class="badge badge-light border mb-1">${total} total</span>`
  }

  lineBadges (lines) {
    if (!lines || !lines.length) { return '<span class="text-muted small">Metro line: unknown</span>' }
    return lines.map(line => {
      const style = LINE_STYLES[line] || { bg: '#666', fg: '#fff' }
      return `<span class="badge mr-2 mb-1" style="background:${style.bg};color:${style.fg}">${escapeHtml(line)}</span>`
    }).join('')
  }

  assetsHtml (assets) {
    if (!assets || !assets.length) { return '' }
    return assets.map(group => {
      const facilities = group.facilities.map(f => this.facilityRow(f)).join('')
      return `
        <div class="mb-2">
          <div class="d-flex justify-content-between font-weight-bold small">
            <span>${escapeHtml(group.type)}s</span>
            <span>${group.available}/${group.total} available</span>
          </div>
          ${facilities}
        </div>`
    }).join('')
  }

  facilityRow (facility) {
    const dot = facility.online === false ? '🔴' : '🟢'
    const nameCell = `<div class="mb-1">${dot} ${escapeHtml(facility.name)}</div>`

    if (facility.total == null) {
      return `<div class="small pl-2 mb-2">${nameCell}<span class="text-muted">no data</span></div>`
    }

    const oversizedTotal = facility.oversized_total || 0
    if (oversizedTotal === 0) {
      return `
        <div class="small pl-2 mb-2">
          ${nameCell}
          <div>${this.countsBadges(facility.available, facility.unavailable, facility.unknown, facility.total)}</div>
        </div>`
    }

    const stdAvail = facility.available - (facility.oversized_available || 0)
    const stdUnavail = facility.unavailable - (facility.oversized_unavailable || 0)
    const stdUnknown = facility.unknown - (facility.oversized_unknown || 0)
    const stdTotal = facility.total - oversizedTotal
    return `
      <div class="small pl-2 mb-2">
        ${nameCell}
        <div class="d-flex align-items-center mb-1">
          <span class="text-muted mr-2" style="min-width: 72px;">Standard</span>
          <span>${this.countsBadges(stdAvail, stdUnavail, stdUnknown, stdTotal)}</span>
        </div>
        <div class="d-flex align-items-center">
          <span class="text-muted mr-2" style="min-width: 72px;">Oversized</span>
          <span>${this.countsBadges(facility.oversized_available || 0, facility.oversized_unavailable || 0, facility.oversized_unknown || 0, oversizedTotal)}</span>
        </div>
      </div>`
  }

  maintenanceSection (station) {
    const m = station.maintenance || {}
    if (!m.threshold_days) { return '' }
    const labels = {
      within: ['badge-success', 'On schedule'],
      at_risk: ['badge-warning', 'Due soon'],
      overdue: ['badge-danger', 'Overdue'],
      never: ['badge-danger', 'Never serviced']
    }
    const [badge, label] = labels[m.state] || ['badge-secondary', m.state]
    const when = m.last_serviced_at
      ? `${this.formatTime(m.last_serviced_at)} (${m.days_since} days ago)`
      : 'no completed maintenance on record'
    return `
      <div class="mb-3">
        <div class="font-weight-bold small mb-1">Maintenance <span class="text-muted font-weight-normal">(every ${m.threshold_days} days)</span></div>
        <div class="pl-2 small d-flex justify-content-between">
          <span>Last serviced: ${when}</span>
          <span class="badge ${badge}">${label}</span>
        </div>
      </div>`
  }

  // Units tracked in Sitetracker: racks, repair stands, Bike & Ride rooms.
  staticAssetsSection (station) {
    const assets = station.static_assets || []
    if (!assets.length) {
      return `
        <div class="mb-2">
          <div class="font-weight-bold small">Static assets</div>
          <div class="small text-muted pl-2">None synced from Sitetracker yet</div>
        </div>`
    }
    const rows = assets.map(a => this.staticAssetRow(a)).join('')
    return `
      <div class="mb-2">
        <div class="font-weight-bold small mb-1">Static assets <span class="text-muted font-weight-normal">(from Sitetracker)</span></div>
        <div class="pl-2">${rows}</div>
      </div>`
  }

  staticAssetRow (asset) {
    const badges = { ok: 'badge-success', attention: 'badge-warning', down: 'badge-danger' }
    const labels = { ok: 'In service', attention: 'Needs attention', down: 'Out of service' }
    const detail = [asset.item, asset.serial ? `s/n ${asset.serial}` : null, asset.installed_on ? `installed ${asset.installed_on}` : null]
      .filter(Boolean).map(escapeHtml).join(' &middot; ')
    return `
      <div class="d-flex justify-content-between align-items-start small mb-1">
        <span>
          <span class="font-weight-bold">${escapeHtml(asset.name || '')}</span>
          ${asset.asset_type ? `<span class="text-muted"> ${escapeHtml(asset.asset_type)}</span>` : ''}
          ${detail ? `<div class="text-muted">${detail}</div>` : ''}
        </span>
        <span class="badge ${badges[asset.status] || 'badge-secondary'}">${labels[asset.status] || asset.status}</span>
      </div>`
  }

  actionButtons (station) {
    const links = station.links || {}
    const buttons = []
    if (links.work_orders) {
      buttons.push(`<a href="${links.work_orders}" class="btn btn-sm btn-outline-primary mr-2">Work orders</a>`)
    }
    if (links.inspections) {
      buttons.push(`<a href="${links.inspections}" class="btn btn-sm btn-outline-primary mr-2">Maintenance history</a>`)
    }
    if (links.sitetracker) {
      buttons.push(`<a href="${links.sitetracker}" target="_blank" rel="noopener" class="btn btn-sm btn-outline-secondary">Open in Sitetracker</a>`)
    }
    if (!buttons.length) { return '' }
    return `<div class="d-flex flex-wrap mb-2">${buttons.join('')}</div>`
  }

  managementLinks (assets) {
    const rows = []
    ;(assets || []).forEach(group => {
      (group.facilities || []).forEach(facility => {
        if (!facility.external_id) { return }
        rows.push(`<a href="https://www.bikelink.org/owners/locations/${facility.external_id}/lockers" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary mr-2 mb-2">${escapeHtml(facility.name)}</a>`)
      })
    })
    if (!rows.length) { return '' }
    return `
      <div class="mb-2">
        <div class="font-weight-bold small mb-1">BikeLink management</div>
        <div class="d-flex flex-wrap">${rows.join('')}</div>
      </div>`
  }

  formatTime (iso) {
    if (!iso) { return 'unknown' }
    const date = new Date(iso)
    return isNaN(date.getTime()) ? 'unknown' : date.toLocaleString()
  }
}
